import * as React from "react";
import { DcfRequest, WebPortalClient } from "webportalclient";
import { DateMatchSortHeader } from "../../Components/DateMatchSortHeader";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { useQuery } from "../../Components/Query";
import { Column, Table } from "../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { DcfRequestStatusEnum, DcfSystemWorkflow } from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { DataCorrectionStatus } from "./DataCorrectionStatus";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { Link } from "react-router-dom";

export const DataCorrectionQueryTable = () => {
    const { data, applyFilters } = useQuery();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [refNumberOptions, setRefNumberOptions] = React.useState([]);
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [statusOptions, setStatusOptions] = React.useState([]);
    const [autoImplementationEnabled, setAutoImplementationEnabled] = React.useState<boolean>();
    const [correctionTypeOptions, setCorrectionTypeOptions] = React.useState([]);

    const uniqWith = (arr, fn) => arr.filter((element, index) => arr.findIndex((step) => fn(element, step)) === index);

    React.useEffect(() => {
        const getReferenceNumbers = async () => {
            const refNumbers = await client.client.getDcfRequests("label,session,visit", `studyId eq ${study.id}`, "label", "session($select=name),visit($select=name)");
            const options = refNumbers.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setRefNumberOptions(options);

            const viOptions = refNumbers.value.filter(x => x.visit).map(v => ({
                value: v.visit.name,
                label: v.visit.name,
            }));

            setVisitOptions(uniqWith(viOptions, (a, b) => a.value === b.value));

            const sesOptions = refNumbers.value.filter(x => x.session).map(s => ({
                value: s.session.name,
                label: s.session.name,
            }));

            setSessionOptions(uniqWith(sesOptions, (a, b) => a.value === b.value));  
        };



        const getSites = async () => {
            const sites = await client.client.getSites("id,number,study", `study/id eq ${study.id}`, "number", "study");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {
            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getStatuses = async () => {
            const craEnabled = await legacyClient.getSetting("dcf.configuration.verificationApprovalEnabled", study.id);

            const statusesResponse = await client.client.getDcfRequestStatuses("id,name", craEnabled ? undefined : `id ne '${DcfRequestStatusEnum.VerificationReview}'`, "order");

            const options = statusesResponse.value.map(s => ({
                value: s.id,
                label: s.name,
            }));

            setStatusOptions(options);
        };

        const getDcfSettings = async () => {
            const dcfSettings = await legacyClient.getStudyDcfSettings(study.id)
            setAutoImplementationEnabled(dcfSettings.SystemWorkflow !== DcfSystemWorkflow.Log);
        }

        getReferenceNumbers();
        getSites();
        getSubjects();
        getStatuses();
        getDcfSettings();
    }, [study.id])

    React.useEffect(() => {
        const getCorrectionTypes = async () => {
            const typesResponse = await client.client.getDcfCorrectionTypes("id,name");

            const options = typesResponse.value.map(s => ({
                value: s.id,
                label: s.name,
            }));

            setCorrectionTypeOptions(options);
        }

        getCorrectionTypes();
    }, []);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const formatEmptyCell = (value: string) => {
        if (value === null || value === undefined)
            return '-';

        return value;
    }

    const formatCorrectionType = (correctionTypeId) => {
        return correctionTypeOptions.find(o => o.value === correctionTypeId).label;
    }

    const renderHamburgerMenu = (row: DcfRequest) => {
        return (
            <div className="text-center">
                <Link to={`/dcf/view/${row.id}`} className="btn btn-primary btn-xs">View</Link>
            </div>
        );
    }

    if (correctionTypeOptions.length === 0) {
        return <LoadingWithMessage message="Loading Requests..." />
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Data Corrections</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button
                            id="clearAllFilters"
                            className="btn btn-primary"
                            onClick={clearAllFilters}
                        >
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            {
                autoImplementationEnabled === true ? null :
                <div className="alert alert-warning">
                    Auto-implementation is disabled, once implemented the changes will only be shown in corrected reports and datasets.
                </div>
            }
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div id="dataCorrectionsTable" className="table-responsive" style={{ minHeight: "900px" }}>
                                <Table rowKeyField="id" data={data}>
                                    <Column field="label" automationId="label" header={<MatchSortHeader automationId="label" title="Reference Number" field="label" options={refNumberOptions} sortField="label" />} formatter={label => label} />
                                    <Column field="site" automationId="site" header={<MatchSortHeader automationId="site" title="Site" field="site/id" options={siteOptions} sortField="site/number" />} formatter={site => site?.number ?? "-"} />
                                    <Column field="subject" automationId="subject" header={<MatchSortHeader automationId="subject" title="Subject" field="subject/id" options={subjectOptions} sortField="subject/number" />} formatter={subject => formatEmptyCell(subject?.number)} />
                                    <Column field="visit" automationId="visit" header={<MatchSortHeader automationId="visit" title="Visit" field="visit/name" options={visitOptions} sortField="visit/name" />} formatter={visit => formatEmptyCell(visit?.name)} />
                                    <Column field="session" automationId="session" header={<MatchSortHeader automationId="session" title="Session" field="session/name" options={sessionOptions} sortField="session/name" />} formatter={session => formatEmptyCell(session?.name)} />
                                    <Column field="created" automationId="created" header={<DateMatchSortHeader automationId="created" title="Created Date & Time" field="created" sortField="created" />} formatter={created => formatDateTime(created)} />
                                    <Column field="correctionTypeId" automationId="correction-type" header={<MatchSortHeader automationId="correction-type" title="Correction Type" field="correctionTypeId" options={correctionTypeOptions} sortField="correctionTypeId" />} formatter={correctionTypeId => formatCorrectionType(correctionTypeId)} />
                                    <Column field="status" automationId="status" header={<MatchSortHeader automationId="status" title="Status" field="status" options={statusOptions} sortField="status" />} formatter={status => <DataCorrectionStatus value={status} />} />
                                    <Column field="" automationId="hamburger-menu" formatter={dcfRequest => renderHamburgerMenu(dcfRequest)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
